import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, formatCalendarDate, Icon, Note, Popover } from '@repo/design-system';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { semantic, space, typeRole } from '@repo/design-tokens';

import { visuallyHidden } from '@repo/ui';

import { addDays, standardName } from './hoursModel';
import { actionProblem, HOURS_STILL_SAVING, type HoursActionResult } from './useOpeningHoursEditor';
import { usePopoverProblem } from './usePopoverProblem';

/**
 * *Remove* on an upcoming standard week (spec twin-details-page-hours-week, R7.6; ruled in by
 * maria@twinlabs.ai, 2026-10-17): the undo for *New Standard Hours From a Date* picked on the wrong
 * date. The week is removed and its dates go back to the week before it, in one save on the server
 * (`extendsPreviousStandard`). No frame draws it; built on the stated default, for Rob.
 */

export interface RemoveHoursProps {
  calendar: AuthoredOpeningHoursVersion;
  /** Every authored version, to name the week that takes the dates back. */
  versions: readonly AuthoredOpeningHoursVersion[];
  busy: boolean;
  onRemove: () => Promise<HoursActionResult>;
  /** Removed; `after` is the hours as the server answered, when it did. */
  onRemoved: (after: readonly AuthoredOpeningHoursVersion[] | null) => void;
  /** An outcome that arrived after the popover was closed, for the step to show and say. */
  onLateProblem: (sentence: string) => void;
  className?: string;
}

export const REMOVE_CHANGED =
  'The hours changed while you were removing these. Check them and remove them again.';
export const REMOVE_UNKNOWN = 'Couldn’t tell whether these hours were removed.';

/**
 * What removing `calendar` does, in words: the confirm's sentence (`ask`) and, once it has happened,
 * the step's announcement (`done`, R9). The second clause appears only when a week ends the day
 * before and so takes the dates back. For `done`, `after` is the server's answer: the clause says
 * what that week now does, and says so plainly if it was not extended (an older middleware ignores
 * the hand-back).
 */
export const removeSentence = (
  calendar: AuthoredOpeningHoursVersion,
  versions: readonly AuthoredOpeningHoursVersion[],
  tense: 'ask' | 'done' = 'ask',
  after: readonly AuthoredOpeningHoursVersion[] | null = null,
): string => {
  // A named set by its name (spec opening-hours-edit-standard-details, R5.3); one without keeps its
  // dates, so two unnamed sets are never both *Standard hours*.
  const from = formatCalendarDate(calendar.effectiveDateFrom);
  const removed = calendar.name
    ? tense === 'ask'
      ? `${standardName(calendar)} is removed.`
      : `${standardName(calendar)} removed.`
    : tense === 'ask'
      ? `The standard hours from ${from} are removed.`
      : `Standard hours from ${from} removed.`;
  const dayBefore = addDays(calendar.effectiveDateFrom, -1);
  const previous = versions.find(v => v.kind === 'standard' && v.effectiveDateTo === dayBefore);
  if (!previous) return removed;
  const named = !!previous.name;
  const them = named
    ? standardName(previous)
    : `The hours from ${formatCalendarDate(previous.effectiveDateFrom)}`;
  const now = after?.find(
    v => v.kind === 'standard' && v.effectiveDateFrom === previous.effectiveDateFrom,
  );
  if (now && now.effectiveDateTo === dayBefore) {
    return `${removed} ${them} still ${named ? 'ends' : 'end'} on ${formatCalendarDate(dayBefore)}.`;
  }
  const end = now ? now.effectiveDateTo : calendar.effectiveDateTo;
  const runsOn = end === null ? 'with no end' : `until ${formatCalendarDate(end)}`;
  const verb = named ? 'runs' : 'run';
  return `${removed} ${them} ${tense === 'ask' ? verb : `now ${verb}`} on ${runsOn}.`;
};

const RemoveHours: React.FC<RemoveHoursProps> = ({
  calendar,
  versions,
  busy,
  onRemove,
  onRemoved,
  onLateProblem,
  className,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();
  const reasonId = useId();
  // The read after a refused action can take this popover away with its owner: someone else removed
  // the week, or added the first one. What it shows goes to the step (R9), and so does an answer
  // still to come.
  const { open, show, close, problem: error, report } = usePopoverProblem(onLateProblem);
  const [sending, setSending] = useState(false);

  const remove = async () => {
    if (sending) return;
    setSending(true);
    report(null);
    const result = await onRemove();
    setSending(false);
    const problem = actionProblem(result, REMOVE_CHANGED, REMOVE_UNKNOWN);
    if (problem === null) {
      close();
      onRemoved(result.status === 'saved' && result.hours ? result.hours.authored.versions : null);
    } else {
      report(problem);
    }
  };

  return (
    <span className={className}>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        iconLeft={<Icon name="trash-can" />}
        aria-label={`Remove the standard hours from ${formatCalendarDate(calendar.effectiveDateFrom)}`}
        aria-disabled={busy || undefined}
        aria-describedby={busy ? reasonId : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        title={busy ? HOURS_STILL_SAVING : undefined}
        onClick={() => {
          if (!busy) show();
        }}
      >
        Remove
      </Button>
      {busy && (
        <span id={reasonId} style={visuallyHidden}>
          {HOURS_STILL_SAVING}
        </span>
      )}
      <Popover
        id={popoverId}
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        title="Remove these hours?"
        align="end"
        initialFocusRef={cancelRef}
        actions={
          <>
            <Button ref={cancelRef} variant="frosted" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              aria-disabled={sending || undefined}
              loading={sending}
              onClick={() => void remove()}
            >
              Remove
            </Button>
          </>
        }
      >
        <div className="body">
          <p className="sentence">{removeSentence(calendar, versions)}</p>
          {/* Polite, so the outcome is heard while focus stays on Remove (R9). */}
          <div role="status">
            {error && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {error}
              </Note>
            )}
          </div>
        </div>
      </Popover>
    </span>
  );
};

export default styled(RemoveHours)`
  display: inline-flex;

  .body {
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
  }

  .sentence {
    margin: 0;
    ${typeRole.body1};
    color: ${semantic.text.primary};
  }
`;
